import React, { Component } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import {
  Alert, Button, Select, Spin,
} from 'antd';
import ModalWrapper from '../../components/modal/ModalWrapper';
import ChartModalReloading from '../../components/modal/ChartModalReloading';
import { captureAnalytics } from '../../actions/preload';

// Own tenant-bound requests outside the form. A destination change unmounts the
// old form (including pending patient searches/duplicate-chart confirmations),
// then mounts a fresh form with this tenant's providers and task templates.
export default class EmaDestinationModal extends Component {
  constructor(props) {
    super(props);
    this.requestId = 0;
    this.selectedIntent = null;
    this.state = {
      credentials: null, selectedId: null, providers: null, templates: null, error: false, submitting: false,
    };
  }

  componentDidMount() { this.loadCredentials(); }

  componentWillUnmount() { this.unmounted = true; this.requestId += 1; }

  loadCredentials = async () => {
    this.requestId += 1;
    const { requestId } = this;
    const previousSelectedId = this.selectedIntent;
    this.setState({
      credentials: null, selectedId: null, providers: null, templates: null, error: false,
    });
    const { data } = this.props.modalType;
    const query = new URLSearchParams({
      receiver_type: data.receiverType || '', receiver_id: data.sendToken || data.record || '',
    });
    try {
      const response = await axios.get(`/api/v1/ema_patients/credentials?${query}`);
      if (this.unmounted || requestId !== this.requestId) return;
      const { credentials, default_credential_id: defaultId } = response.data;
      this.setState({ credentials });
      const selectedId = credentials.find((credential) => credential.id === previousSelectedId)?.id
        || credentials.find((credential) => credential.id === defaultId)?.id
        || (credentials.length === 1 ? credentials[0].id : null);
      this.selectedIntent = selectedId;
      if (selectedId) await this.loadDestination(selectedId);
    } catch {
      if (!this.unmounted && requestId === this.requestId) this.setState({ error: true });
    }
  };

  loadDestination = async (selectedId) => {
    this.selectedIntent = selectedId;
    this.requestId += 1;
    const { requestId } = this;
    this.setState({
      selectedId, providers: null, templates: null, error: false,
    });
    const { data } = this.props.modalType;
    try {
      const [providersResponse, templatesResponse] = await Promise.all([
        this.props.getEmaTaskProviders(data.sendToken || data.record, selectedId),
        this.props.getTaskTemplatesByQuery(`?email=${encodeURIComponent(localStorage.getItem('m8_uid') || '')}`),
      ]);
      if (this.unmounted || requestId !== this.requestId) return;
      if (!Array.isArray(providersResponse?.data) || !Array.isArray(templatesResponse?.data?.ema_task_templates)) throw new Error('EMA options unavailable');
      const providers = providersResponse.data;
      const names = new Set(providers.map((provider) => provider.name));
      const templates = templatesResponse.data.ema_task_templates.filter((template) => (
        this.state.credentials.length === 1 || (
          template.assign_to_as_array?.length > 0
          && template.assign_to_as_array.every((name) => names.has(name))
          && (!template.assign_to_filters || Object.keys(template.assign_to_filters).length === 0)
        )
      ));
      this.setState({ providers, templates: { ...templatesResponse.data, ema_task_templates: templates } });
    } catch {
      if (!this.unmounted && requestId === this.requestId) this.setState({ error: true });
    }
  };

  retryLoading = () => {
    captureAnalytics('ema_destination_load_retried', {});
    return this.loadCredentials();
  };

  changeDestination = (selectedId) => {
    if (this.state.submitting) return;
    captureAnalytics('ema_destination_changed', { destination_count: this.state.credentials.length });
    this.loadDestination(selectedId);
  };

  submit = async (action, payload, selectedId, requestId) => {
    // Fence stale forms and confirmations even if a callback escaped unmount.
    if (this.unmounted || requestId !== this.requestId || selectedId !== this.state.selectedId || this.state.submitting) return { canceled: true };
    this.setState({ submitting: true });
    try {
      return await action({ ...payload, emrCredentialId: selectedId });
    } finally {
      if (!this.unmounted) this.setState({ submitting: false });
    }
  };

  render() {
    const {
      credentials, selectedId, providers, templates, error, submitting,
    } = this.state;
    const { ModalComponent } = this.props;
    const multiple = credentials?.length > 1;
    const picker = multiple && (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>EMA destination</div>
        <Select id="ema-destination" aria-label="EMA destination" style={{ width: '100%' }} value={selectedId || undefined} placeholder="Choose an EMA" disabled={submitting} onChange={this.changeDestination}>
          {credentials.map((credential) => <Select.Option key={credential.id} value={credential.id}>{credential.tenant ? `${credential.name} (${credential.tenant})` : credential.name}</Select.Option>)}
        </Select>
        <div style={{ fontSize: 12, color: '#666', marginTop: 6 }}>Changing EMA resets this form. Patient search and assignees follow the selected EMA.</div>
      </div>
    );
    let loadingContent = <Alert type="info" message={credentials?.length ? 'Choose the EMA before searching for a patient.' : 'No verified EMA destination is available.'} />;
    if (!credentials || selectedId) loadingContent = <Spin />;
    if (error) loadingContent = <Alert type="error" message="Could not load EMA destinations or options." action={<Button onClick={this.retryLoading}>Try again</Button>} />;
    if (!providers || error) {
      // Reopened for the record the slide-out moved to: keep the reload placeholder
      // up while this record's EMA options load, unless there is a destination to pick
      // or nothing left to load (an empty list falls through to the unavailable message).
      const loading = !credentials || Boolean(selectedId);
      if (this.props.modalType.data?.reloadedForRecord && loading && !error && !multiple) {
        return (
          <ChartModalReloading
            modalType={{ data: { reloadType: this.props.modalType.type } }}
            hideModal={this.props.hideModal}
          />
        );
      }
      return (
        <ModalWrapper customContentStyle={{}} modalTitle="Choose EMA destination" hideModal={this.props.hideModal} dismiss="Cancel">
          {picker}
          {loadingContent}
        </ModalWrapper>
      );
    }
    const { requestId } = this;
    const selected = credentials.find((credential) => credential.id === selectedId);
    const appendDestination = (query) => `${query || '?'}${query && query !== '?' ? '&' : ''}emr_credential_id=${selectedId}`;
    return (
      <ModalComponent
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...this.props}
        key={selectedId}
        modalType={{ ...this.props.modalType, data: { ...this.props.modalType.data, apiCredentials: selected.api_credentials, ...(multiple && { providerName: '', practitionerName: '' }) } }}
        emaDestinationPicker={picker}
        multipleEmaDestinations={multiple}
        emaScopedProviders={providers}
        emaScopedTaskTemplates={templates}
        getEmaPatients={(query) => this.props.getEmaPatients(appendDestination(query))}
        getEmaTaskProviders={async () => ({ data: providers })}
        getTaskTemplatesByQuery={async () => ({ data: templates })}
        triggerAutoUpload={(payload) => this.submit(this.props.triggerAutoUpload, payload, selectedId, requestId)}
        triggerAutoUploadAndTaskCreation={(payload) => this.submit(this.props.triggerAutoUploadAndTaskCreation, payload, selectedId, requestId)}
      />
    );
  }
}

EmaDestinationModal.propTypes = {
  ModalComponent: PropTypes.elementType.isRequired,
  modalType: PropTypes.object.isRequired,
  hideModal: PropTypes.func.isRequired,
  getEmaTaskProviders: PropTypes.func.isRequired,
  getTaskTemplatesByQuery: PropTypes.func.isRequired,
  getEmaPatients: PropTypes.func.isRequired,
  triggerAutoUpload: PropTypes.func.isRequired,
  triggerAutoUploadAndTaskCreation: PropTypes.func.isRequired,
};
